import { Editor } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import { markRaw } from "@vue/reactivity";

// A region holds prose, nothing else. No headings, tables or images: the
// template owns the layout, the editor only owns the words inside a zone.
function regionExtensions() {
  return [
    Document,
    Paragraph,
    Text,
    Bold,
    Italic,
    Underline,
    Link.configure({ openOnClick: false }),
    BulletList,
    OrderedList,
    ListItem,
    HardBreak,
    History
  ];
}

/**
 * Register the Alpine.js `documentRegionEditor` component.
 *
 * Renders a document into an iframe built server-side from the same CSS the PDF
 * uses, then mounts one Tiptap editor per [data-editable] zone inside that
 * iframe. Everything outside a zone is display-only.
 *
 * Expects: x-ref="frame" (the iframe), x-ref="form" (a div wrapping the hidden
 * inputs, inside a form_with), and data-srcdoc / data-regions /
 * data-link-prompt on the component root.
 */
export default function registerDocumentRegionEditor(Alpine) {
  Alpine.data("documentRegionEditor", function () {
    return {
      editors: {},
      activeRegion: null,
      activeNode: null,
      toolbarTop: 0,
      toolbarLeft: 0,
      tick: 0,
      reposition: null,

      init() {
        this.regionIds = JSON.parse(this.$el.dataset.regions || "[]");
        const frameHtml = this.$el.dataset.srcdoc || "";

        const frame = this.$refs.frame;
        frame.addEventListener("load", () => this.mountRegions());
        frame.srcdoc = frameHtml;

        this.$refs.form.closest("form")?.addEventListener("submit", () => this.syncInputs());

        // The toolbar is position: fixed, so its coordinates go stale the
        // moment the page scrolls or the viewport changes size.
        this.reposition = () => this.positionToolbar();
        window.addEventListener("scroll", this.reposition, { passive: true });
        window.addEventListener("resize", this.reposition);
      },

      destroy() {
        this.destroyEditors();
        if (!this.reposition) return;
        window.removeEventListener("scroll", this.reposition);
        window.removeEventListener("resize", this.reposition);
        this.reposition = null;
      },

      // Separate from destroy() because mountRegions() drops stale editors on a
      // repeat `load` event — the window listeners must outlive that.
      destroyEditors() {
        Object.values(this.editors).forEach((editor) => editor.destroy());
        this.editors = {};
        this.activeRegion = null;
        this.activeNode = null;
      },

      // Tears down any previously mounted editors first, so a repeat `load`
      // event (Turbo restoring a cached page, a genuine srcdoc reassignment)
      // can't leak the old ProseMirror views or duplicate region content —
      // it just remounts fresh, working editors.
      mountRegions() {
        this.destroyEditors();

        const doc = this.$refs.frame.contentDocument;
        if (!doc) return;

        this.regionIds.forEach((id) => {
          const node = doc.querySelector(`[data-editable="${id}"]`);
          if (!node) return;

          // Tiptap appends its editor DOM into `element` without clearing what
          // is already there, so the server-rendered prose would stay on screen
          // beside the editable copy. Take the HTML, then empty the node before
          // handing it over.
          const html = node.innerHTML;
          node.innerHTML = "";

          const editor = markRaw(
            new Editor({
              element: node,
              content: html,
              extensions: regionExtensions(),
              onFocus: () => this.focusRegion(id, node),
              onTransaction: () => {
                this.tick += 1;
                this.resizeFrame();
              }
            })
          );
          this.editors[id] = editor;
        });

        this.resizeFrame();
      },

      // The iframe has no scrollbars of its own — it grows to fit the document
      // so the page scrolls as one surface.
      resizeFrame() {
        const frame = this.$refs.frame;
        const doc = frame.contentDocument;
        if (!doc?.documentElement) return;
        frame.style.height = `${doc.documentElement.scrollHeight}px`;
      },

      focusRegion(id, node) {
        this.activeRegion = id;
        this.activeNode = node;
        this.positionToolbar();
      },

      // The toolbar is position: fixed, so it takes viewport coordinates.
      // Both rects are already viewport-relative and the node's is relative to
      // the frame's own viewport, so the two simply add — adding scroll offsets
      // on top would double-count them.
      positionToolbar() {
        if (!this.activeRegion || !this.activeNode) return;

        const frameRect = this.$refs.frame.getBoundingClientRect();
        const nodeRect = this.activeNode.getBoundingClientRect();
        this.toolbarTop = frameRect.top + nodeRect.top - 44;
        this.toolbarLeft = frameRect.left + nodeRect.left;
      },

      current() {
        return this.activeRegion ? this.editors[this.activeRegion] : null;
      },

      run(command) {
        const editor = this.current();
        if (!editor) return;
        command(editor.chain().focus()).run();
      },

      toggleBold() {
        this.run((c) => c.toggleBold());
      },
      toggleItalic() {
        this.run((c) => c.toggleItalic());
      },
      toggleUnderline() {
        this.run((c) => c.toggleUnderline());
      },
      toggleBulletList() {
        this.run((c) => c.toggleBulletList());
      },
      toggleOrderedList() {
        this.run((c) => c.toggleOrderedList());
      },

      setLink() {
        const editor = this.current();
        if (!editor) return;
        const previous = editor.getAttributes("link").href || "";
        const url = window.prompt(this.$el.dataset.linkPrompt || "URL", previous);
        if (url === null) return;
        if (url === "") {
          editor.chain().focus().unsetLink().run();
          return;
        }
        editor.chain().focus().setLink({ href: url }).run();
      },

      isActive(name) {
        this.tick;
        return this.current()?.isActive(name) || false;
      },

      syncInputs() {
        Object.entries(this.editors).forEach(([id, editor]) => {
          const input = this.$refs.form.querySelector(`input[name="document[regions][${id}]"]`);
          if (input) input.value = editor.getHTML();
        });
      }
    };
  });
}
